// 通知模块API接口

import { BaseApi } from "../../../utils/request";
import { PaginatedResponse } from "../../../types/common";
import type {
  Notification,
  NoticePageRequest,
  NoticePageApiResponse,
  NoticeActionApiResponse,
} from "../types";

/**
 * 通知API类
 * 统一管理系统通知和消息中心相关的API接口
 */
export class NotificationApi extends BaseApi {
  /**
   * 获取通知列表
   * @param params 查询参数
   * @returns Promise<PaginatedResponse<Notification>>
   */
  async getNotifications(
    params: {
      page?: number;
      pageSize?: number;
      unreadOnly?: boolean;
    } = {}
  ): Promise<PaginatedResponse<Notification>> {
    const response = await this.get<PaginatedResponse<Notification>>(
      "/api/notification/list",
      params,
      {
        cache: true,
        cacheTime: 1 * 60 * 1000, // 1分钟缓存
        alert: false,
      }
    );
    return response.data;
  }

  /**
   * 标记通知为已读
   * @param notificationId 通知ID
   */
  async markAsRead(notificationId: string): Promise<void> {
    const response = await this.post<void>(
      `/api/notification/${notificationId}/read`
    );
    return response.data;
  }

  /**
   * 标记所有通知为已读
   */
  async markAllAsRead(): Promise<void> {
    const response = await this.post<void>("/api/notification/read-all");
    return response.data;
  }

  /**
   * 删除通知
   * @param notificationId 通知ID
   */
  async deleteNotification(notificationId: string): Promise<void> {
    const response = await this.del<void>(
      `/api/notification/${notificationId}`
    );
    return response.data;
  }

  /**
   * 获取未读通知数量
   * @returns Promise<{ count: number }>
   */
  async getUnreadCount(): Promise<{ count: number }> {
    const response = await this.get<{ count: number }>(
      "/api/notification/unread-count",
      {},
      {
        cache: true,
        cacheTime: 30 * 1000, // 30秒缓存
        alert: false,
      }
    );
    return response.data;
  }

  // ========== 消息中心相关API ==========

  /**
   * 获取通知/公告分页列表
   * @param params 分页参数
   * @returns Promise<NoticePageApiResponse>
   */
  async getNoticePage(
    params: NoticePageRequest
  ): Promise<NoticePageApiResponse> {
    return this.post<any, true>("/api/user/notice_page", {
      query: params,
    });
  }

  /**
   * 清空所有通知
   * @returns Promise<NoticeActionApiResponse>
   */
  async clearAllNotices(): Promise<NoticeActionApiResponse> {
    return this.post<any, true>("/api/user/notice_clear", {});
  }

  /**
   * 标记站内信为已读（打开详情时上报：read + pushed）
   * @param id 通知ID
   */
  async markNoticeRead(id: string): Promise<NoticeActionApiResponse> {
    return this.post<any, true>(
      `/api/app_user_notice/markRead?id=${id}`,
      {}
    );
  }

  /**
   * 上报站内信跳转按钮点击（clicked + 补已读 + pushed）
   * @param id 通知ID
   */
  async trackNoticeClick(id: string): Promise<NoticeActionApiResponse> {
    return this.post<any, true>(
      `/api/app_user_notice/trackClick?id=${id}`,
      {}
    );
  }

  /**
   * 上报站内信弹窗已展示（仅 pushed，不计 read）
   * @param id 通知ID
   */
  async markNoticePopupShown(id: string): Promise<NoticeActionApiResponse> {
    return this.post<any, true>(
      `/api/app_user_notice/markPopupShown?id=${id}`,
      {}
    );
  }

  /**
   * 删除单条通知
   * @param id 通知ID
   * @returns Promise<NoticeActionApiResponse>
   */
  async deleteNotice(id: string): Promise<NoticeActionApiResponse> {
    return this.post<any, true>("/api/user/notice_read", { id });
  }
}

// 创建API实例
export const notificationApi = new NotificationApi();

// ========== 便捷方法导出 ==========

// 消息中心相关便捷方法
export const getNoticePage = (params: NoticePageRequest) =>
  notificationApi.getNoticePage(params);

export const clearAllNotices = () => notificationApi.clearAllNotices();

export const markNoticeRead = (id: string) =>
  notificationApi.markNoticeRead(id);

export const trackNoticeClick = (id: string) =>
  notificationApi.trackNoticeClick(id);

export const markNoticePopupShown = (id: string) =>
  notificationApi.markNoticePopupShown(id);

export const deleteNotice = (id: string) => notificationApi.deleteNotice(id);
